import {
  CmpEventAttendeeInput,
  CmpEventCustomFieldAnswerInput,
  CmpEventRegistrationStatusEnum,
  CmpEventRegistrationFieldsFragment,
  CmpEventPaymentStatusEnum,
  CmpInvoiceStatusEnum,
  CmpPaymentChoiceTypeEnum,
  CmpEventSponsorshipRequestFieldsFragment,
  UpdateCmpEventRegistrationInput,
  UpdateCmpEventSponsorshipRequestInput,
} from "../../../../graphql/types";
import { UserFacingError } from "../../../../utils/userFacingError";

// The saved attendees no longer match the edit form (another change landed).
const ATTENDEE_LIST_CHANGED_TEXT =
  "The attendee list changed since you opened it. Close this window, refresh the page, and make your changes again.";

export type EventSalesCustomFieldAnswerSource = {
  fieldId: string;
  labelSnapshot?: string | null;
  value: string;
};

export type EventSalesAttendeeSource = {
  companyName?: string | null;
  id: string;
  firstName: string;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  ticketTypeId: string;
  status: CmpEventRegistrationStatusEnum;
  customFieldAnswers: ReadonlyArray<EventSalesCustomFieldAnswerSource>;
};

export type EventSalesAttendeeNameEdit = {
  companyName?: string | null;
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  customFieldAnswers?: Readonly<Record<string, string>>;
  /** Additional Details; undefined keeps the saved note. */
  staffNotes?: string;
};

/** Canceled rows remain in storage for audit and ticket-unit grouping. */
export const getActiveEventAttendees = <
  T extends { status: CmpEventRegistrationStatusEnum },
>(attendees: readonly T[]): T[] =>
  attendees.filter(
    ({ status }) => status !== CmpEventRegistrationStatusEnum.Canceled,
  );

export const getEventSponsorshipAttendeeCount = (
  sponsorship: Pick<
    CmpEventSponsorshipRequestFieldsFragment,
    "includedAttendees" | "includedAttendeeQuantity"
  >,
): number =>
  sponsorship.includedAttendees.some(
    ({ status }) => status === CmpEventRegistrationStatusEnum.Canceled,
  )
    ? getActiveEventAttendees(sponsorship.includedAttendees).length
    : sponsorship.includedAttendeeQuantity;

export const getEventRegistrationTicketCount = (
  registration: Pick<
    CmpEventRegistrationFieldsFragment,
    "status" | "attendees" | "ticketQuantity"
  >,
): number =>
  registration.status === CmpEventRegistrationStatusEnum.Canceled
    ? 0
    : registration.ticketQuantity ?? getActiveEventAttendees(registration.attendees).length;

export const getEventRegistrationReservedAttendeeCount = (
  registration: Pick<
    CmpEventRegistrationFieldsFragment,
    "status" | "attendees" | "reservedAttendeeCount"
  >,
): number =>
  registration.status === CmpEventRegistrationStatusEnum.Canceled
    ? 0
    : registration.reservedAttendeeCount ?? getActiveEventAttendees(registration.attendees).length;

export const canDeleteFreeEventRegistrationAttendees = (
  registration: Pick<
    CmpEventRegistrationFieldsFragment,
    "status" | "totalAmountInCents" | "paymentChoice" | "paymentStatus" |
    "paymentId" | "payments" | "invoiceId" | "invoice"
  > & { attendees?: readonly Pick<EventSalesAttendeeSource, "status">[] | null },
  attendee?: Pick<EventSalesAttendeeSource, "ticketTypeId">,
): boolean => {
  // GrowthZone imports (Ajax) carry past attendance (Attended, No-show). The
  // backend cannot remove attendees from those sales, so Delete is not offered
  // (Oct 15 2026).
  if ((registration.attendees || []).some(({ status }) =>
    status === CmpEventRegistrationStatusEnum.Attended ||
    status === CmpEventRegistrationStatusEnum.NoShow)) {
    return false;
  }
  if (registration.totalAmountInCents > 0 && attendee && registration.invoice &&
    registration.status === CmpEventRegistrationStatusEnum.Confirmed) {
    const invoice = registration.invoice;
    const line = (invoice.lineItems || []).find(item => item.id === `${invoice.id}#event-ticket#${attendee.ticketTypeId}`);
    // A 100% staff discount prints as the full-price line plus its discount
    // line (design (11)); together they are exactly $0 with no tax.
    const discountLine = (invoice.lineItems || []).find(item => item.id === `${invoice.id}#event-ticket-discount#${attendee.ticketTypeId}`);
    const lineIsFree = Boolean(line && (discountLine
      ? line.totalAmountInCents + discountLine.totalAmountInCents === 0 &&
        line.taxAmountInCents === 0 && discountLine.taxAmountInCents === 0
      : line.unitPriceInCents === 0 && line.taxAmountInCents === 0 && line.totalAmountInCents === 0));
    return Boolean(registration.invoiceId === invoice.id &&
      [CmpInvoiceStatusEnum.Paid, CmpInvoiceStatusEnum.Unpaid].includes(invoice.invoiceStatus) &&
      !invoice.refundedAmountInCents && !invoice.creditedAmountInCents && !invoice.creditForInvoiceId &&
      lineIsFree);
  }
  if (
    registration.status !== CmpEventRegistrationStatusEnum.Confirmed ||
    registration.totalAmountInCents !== 0 ||
    registration.paymentChoice.type !== CmpPaymentChoiceTypeEnum.Free ||
    registration.paymentStatus !== CmpEventPaymentStatusEnum.NotRequired ||
    registration.paymentId ||
    registration.payments.length > 0
  ) {
    return false;
  }
  const invoice = registration.invoice;
  if (!invoice) return !registration.invoiceId;
  // Staff ticket invoices keep their total only in the pricing snapshot, so
  // the API returns totalAmountInCents as null. Their lines still add up to
  // $0 (Ajax invoice 259, Oct 15 2026). The backend checks the snapshot.
  const invoiceTotalInCents = invoice.totalAmountInCents ??
    (invoice.lineItems || []).reduce((sum, line) => sum + line.totalAmountInCents, 0);
  return (
    registration.invoiceId === invoice.id &&
    invoice.paymentChoice.type === CmpPaymentChoiceTypeEnum.Free &&
    invoiceTotalInCents === 0 &&
    invoice.amountDueInCents === 0 &&
    !invoice.refundedAmountInCents &&
    !invoice.refundedAtUtcMilli &&
    !invoice.creditedAmountInCents &&
    !invoice.creditForInvoiceId &&
    !invoice.paymentPlan?.amountPaidInCents &&
    [
      CmpInvoiceStatusEnum.Paid,
      CmpInvoiceStatusEnum.Unpaid,
      CmpInvoiceStatusEnum.Waived,
    ].includes(invoice.invoiceStatus)
  );
};

export const areEventAttendeeDeletionsConfirmed = ({
  attendees,
  deletedAttendeeIds,
}: {
  attendees: readonly Pick<EventSalesAttendeeSource, "id" | "status">[];
  deletedAttendeeIds: readonly string[];
}): boolean =>
  deletedAttendeeIds.every((id) =>
    attendees.some((attendee) =>
      attendee.id === id && attendee.status === CmpEventRegistrationStatusEnum.Canceled,
    ),
  );

/** Attendee cancellation must not update the payer or billing snapshot. */
export const buildGuardedEventAttendeeUpdateInput = <
  Input extends Pick<UpdateCmpEventRegistrationInput, "billing">,
>({
  input,
  deletedAttendeeIds,
  expectedUpdatedAtUtcMilli,
}: {
  input: Input;
  deletedAttendeeIds: string[];
  expectedUpdatedAtUtcMilli?: number | null;
}) => {
  const { billing, ...attendeeInput } = input;
  return { ...attendeeInput, deletedAttendeeIds, expectedUpdatedAtUtcMilli };
};

type SerializeEventCustomFieldChoiceValueInput = Readonly<{
  choices: readonly string[];
  selectedValues: readonly string[];
}>;

// Claude Design (8): the Speaker Question answer allows 250 characters.
export const eventSpeakerQuestionMaxLength = 250;

export const serializeEventCustomFieldChoiceValue = ({
  choices,
  selectedValues,
}: SerializeEventCustomFieldChoiceValueInput): string => {
  const selected = new Set(
    selectedValues.map((value) => value.trim()).filter(Boolean),
  );
  const configuredChoices = new Set(
    choices.map((choice) => choice.trim()).filter(Boolean),
  );

  // Match the public form's choice order, while retaining legacy values until
  // staff remove them from this field.
  return [
    ...Array.from(configuredChoices).filter((choice) => selected.has(choice)),
    ...Array.from(selected).filter((value) => !configuredChoices.has(value)),
  ].join(", ");
};

type ParseEventCustomFieldChoiceValueInput = Readonly<{
  choices: readonly string[];
  value?: string | null;
}>;

type ParsedEventCustomFieldChoiceValue = {
  selected: string[];
  extras: string[];
};

export const parseEventCustomFieldChoiceValue = ({
  choices,
  value,
}: ParseEventCustomFieldChoiceValueInput): ParsedEventCustomFieldChoiceValue => {
  const savedValues = new Set(
    (value || "")
      .split(", ")
      .map((savedValue) => savedValue.trim())
      .filter(Boolean),
  );
  const configuredChoices = new Set(
    choices.map((choice) => choice.trim()).filter(Boolean),
  );

  return {
    selected: Array.from(configuredChoices).filter((choice) =>
      savedValues.has(choice),
    ),
    extras: Array.from(savedValues).filter(
      (savedValue) => !configuredChoices.has(savedValue),
    ),
  };
};

type BuildEventAttendeeCustomFieldAnswerInputsInput = Readonly<{
  existingAnswers: ReadonlyArray<EventSalesCustomFieldAnswerSource>;
  editedAnswers?: Readonly<Record<string, string>>;
}>;

export const buildEventAttendeeCustomFieldAnswerInputs = ({
  existingAnswers,
  editedAnswers = {},
}: BuildEventAttendeeCustomFieldAnswerInputsInput): CmpEventCustomFieldAnswerInput[] => {
  const lastSavedValuesByFieldId = new Map(
    existingAnswers.map((answer) => [answer.fieldId, answer.value]),
  );
  const answers = existingAnswers.flatMap((answer) => {
    if (
      !Object.prototype.hasOwnProperty.call(editedAnswers, answer.fieldId) ||
      editedAnswers[answer.fieldId] ===
        lastSavedValuesByFieldId.get(answer.fieldId)
    ) {
      // The modal prefills all answers into a map. An unchanged value must
      // preserve the source entries, including legacy duplicate field IDs.
      return [{ fieldId: answer.fieldId, value: answer.value }];
    }

    const value = editedAnswers[answer.fieldId].trim();
    return value ? [{ fieldId: answer.fieldId, value }] : [];
  });

  Object.entries(editedAnswers).forEach(([fieldId, editedValue]) => {
    const value = editedValue.trim();
    if (!lastSavedValuesByFieldId.has(fieldId) && value) {
      answers.push({ fieldId, value });
    }
  });

  return answers;
};

export const toCmpEventCustomFieldAnswerInputs = ({
  answers,
}: {
  answers: ReadonlyArray<EventSalesCustomFieldAnswerSource>;
}): CmpEventCustomFieldAnswerInput[] =>
  answers.map((answer) => ({
    fieldId: answer.fieldId,
    value: answer.value,
  }));

/**
 * Applies name edits in the original attendee order. Attendee identity,
 * allocation fields, lifecycle state, and answers survive this nonfinancial
 * update. The modal can also update email and phone while keeping legacy blank
 * values optional.
 */
export const buildCmpEventAttendeeNameUpdateInputs = ({
  attendees,
  edits,
  deletedAttendeeIds = [],
}: {
  attendees: ReadonlyArray<EventSalesAttendeeSource>;
  edits: ReadonlyArray<EventSalesAttendeeNameEdit>;
  deletedAttendeeIds?: readonly string[];
}): CmpEventAttendeeInput[] => {
  if (attendees.length !== edits.length) {
    throw new UserFacingError(ATTENDEE_LIST_CHANGED_TEXT);
  }

  const editsByAttendeeId = new Map<string, EventSalesAttendeeNameEdit>();
  edits.forEach((edit) => {
    if (editsByAttendeeId.has(edit.id)) {
      throw new UserFacingError(ATTENDEE_LIST_CHANGED_TEXT);
    }
    editsByAttendeeId.set(edit.id, edit);
  });

  return attendees.map((attendee) => {
    const edit = editsByAttendeeId.get(attendee.id);
    if (!edit) {
      throw new UserFacingError(ATTENDEE_LIST_CHANGED_TEXT);
    }

    if (
      attendee.status === CmpEventRegistrationStatusEnum.Canceled ||
      deletedAttendeeIds.includes(attendee.id)
    ) {
      return {
        id: attendee.id,
        firstName: attendee.firstName,
        lastName: attendee.lastName,
        email: attendee.email,
        phone: attendee.phone,
        companyName: attendee.companyName,
        ticketTypeId: attendee.ticketTypeId,
        status: attendee.status,
        customFieldAnswers: toCmpEventCustomFieldAnswerInputs({
          answers: attendee.customFieldAnswers,
        }),
      };
    }

    return {
      id: attendee.id,
      firstName: edit.firstName,
      lastName: edit.lastName || undefined,
      email: edit.email?.trim() || undefined,
      phone: edit.phone?.trim() || undefined,
      companyName: edit.companyName === undefined ? attendee.companyName : edit.companyName?.trim() || null,
      ticketTypeId: attendee.ticketTypeId,
      status: attendee.status,
      customFieldAnswers: buildEventAttendeeCustomFieldAnswerInputs({
        existingAnswers: attendee.customFieldAnswers,
        editedAnswers: edit.customFieldAnswers,
      }),
      // Sent only when edited; the backend keeps the saved note otherwise.
      ...(edit.staffNotes !== undefined ? { staffNotes: edit.staffNotes.trim() } : {}),
    };
  });
};

export const buildCmpEventSponsorshipAttendeeUpdateInputs = ({
  sponsorship,
  edits,
  deletedAttendeeIds,
}: {
  sponsorship: Pick<
    CmpEventSponsorshipRequestFieldsFragment,
    "includedAttendees" | "additionalAttendees"
  >;
  edits: ReadonlyArray<EventSalesAttendeeNameEdit>;
  deletedAttendeeIds?: readonly string[];
}): Pick<
  UpdateCmpEventSponsorshipRequestInput,
  "includedAttendees" | "additionalAttendees"
> => {
  const attendees = buildCmpEventAttendeeNameUpdateInputs({
    attendees: [
      ...sponsorship.includedAttendees,
      ...(sponsorship.additionalAttendees || []),
    ],
    edits,
    deletedAttendeeIds,
  });

  return {
    includedAttendees: attendees.slice(0, sponsorship.includedAttendees.length),
    additionalAttendees: attendees.slice(sponsorship.includedAttendees.length),
  };
};
